const apiBaseUrl =
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_API_URL ?? "/api";
const CSRF_KEY = "contractor-quoting-csrf";

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export function setCsrfToken(token: string): void {
  sessionStorage.setItem(CSRF_KEY, token);
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const csrf = !["GET", "HEAD", "OPTIONS"].includes(method)
    ? sessionStorage.getItem(CSRF_KEY)
    : null;
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData))
    headers.set("content-type", "application/json");
  if (csrf) headers.set("x-csrf-token", csrf);
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiError(
      response.ok ? 502 : response.status,
      "invalid_api_response",
      "The service is temporarily unavailable. Please try again.",
    );
  }
  let body: T | { error?: { code?: string; message?: string; details?: unknown } };
  try {
    body = (await response.json()) as
      | T
      | {
          error?: { code?: string; message?: string; details?: unknown };
        };
  } catch {
    throw new ApiError(
      response.ok ? 502 : response.status,
      "invalid_api_response",
      "The service is temporarily unavailable. Please try again.",
    );
  }
  if (!response.ok) {
    const error = (
      body as {
        error?: { code?: string; message?: string; details?: unknown };
      }
    ).error;
    throw new ApiError(
      response.status,
      error?.code ?? "request_failed",
      error?.message ?? `Request failed (${response.status})`,
      error?.details,
    );
  }
  return body as T;
}

export async function ensureCsrf(): Promise<string> {
  const result = await apiFetch<{ csrfToken: string }>("/v1/auth/csrf", { method: "POST" });
  setCsrfToken(result.csrfToken);
  return result.csrfToken;
}
